// ContactFormik.jsx
import React, { useEffect } from "react";
import { Grid, Icon, TextField } from "@mui/material";
import MDButton from "components/MDButton";
import { useFormikContext } from "formik";
import { useParams } from "react-router-dom";
import { fetchById } from "services/ContactMessages";
import { useMaterialUIController, setErrorSB } from "context";
import MDTypography from "components/MDTypography";
import useLocale from "context/useLocale";
import Moment from "react-moment";

function ContactFormik() {
  const locale = useLocale();
  const { setFieldValue, handleSubmit, handleBlur, values, isSubmitting } = useFormikContext();
  const { id } = useParams();
  const [, dispatch] = useMaterialUIController();

  // Fetch the contact message details when the component mounts.
  useEffect(() => {
    if (id) {
      fetchById(id).then(
        (res) => {
          const { data } = res.data;
          setFieldValue("phone", data.phone);
          setFieldValue("email", data.email);
          setFieldValue("subject", data.subject);
          setFieldValue("message", data.message);
          setFieldValue("created_at", data.created_at);
          setFieldValue("is_read", data.is_read ? 1 : 0);
        },
        (err) => setErrorSB(dispatch, `${err.response.data.error.message}`)
      );
    }
  }, [id, setFieldValue, dispatch]);

  // Function to toggle the read/unread status.
  const toggleReadStatus = () => {
    setFieldValue("is_read", values.is_read ? 0 : 1);
  };

  return (
    <form onSubmit={handleSubmit}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <MDTypography component="p" variant="button" color="text" fontWeight="medium">
            {locale("phone")}
          </MDTypography>
          <TextField
            variant="outlined"
            fullWidth
            id="phone"
            value={values.phone || ""}
            onBlur={handleBlur}
            readonly
          />
        </Grid>
        <Grid item xs={12}>
          <MDTypography component="p" variant="button" color="text" fontWeight="medium">
            {locale("email")}
          </MDTypography>
          <TextField
            variant="outlined"
            fullWidth
            id="email"
            value={values.email || ""}
            onBlur={handleBlur}
            readonly
          />
        </Grid>
        <Grid item xs={12}>
          <MDTypography component="p" variant="button" color="text" fontWeight="medium">
            {locale("subject")}
          </MDTypography>
          <TextField
            variant="outlined"
            fullWidth
            id="subject"
            value={values.subject || ""}
            onBlur={handleBlur}
            readonly
          />
        </Grid>
        <Grid item xs={12}>
          <MDTypography component="p" variant="button" color="text" fontWeight="medium">
            {locale("message")}
          </MDTypography>
          <TextField
            variant="outlined"
            fullWidth
            multiline
            rows={4}
            id="message"
            value={values.message || ""}
            onBlur={handleBlur}
            readonly
          />
        </Grid>
        <Grid item xs={12}>
          <MDTypography component="p" variant="button" color="text" fontWeight="medium">
            {locale("createdAt")}
          </MDTypography>
          <Moment format="DD-MMM-YYYY, hh:mm A">{values.created_at || ""}</Moment>
        </Grid>
      </Grid>
      <Grid container spacing={2} justifyContent="space-between" mt={3}>
        <Grid item>
          <MDButton
            type="button"
            variant="contained"
            color={values.is_read ? "warning" : "info"}
            onClick={toggleReadStatus}
            disabled={isSubmitting}
            endIcon={<Icon>{values.is_read ? "visibility" : "visibility_off"}</Icon>}
          >
            {values.is_read ? locale("mark_as_unread") : locale("mark_as_read")}
          </MDButton>
        </Grid>
        <Grid item>
          <MDButton
            type="submit"
            variant="contained"
            color="info"
            endIcon={<Icon>send</Icon>}
            disabled={isSubmitting}
          >
            {locale("submit")}
          </MDButton>
        </Grid>
      </Grid>
    </form>
  );
}

export default ContactFormik;
